import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { TokenStorageService } from './token-storage.service';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class SocketService {
  private socket: Socket | null = null;
  private connected$ = new BehaviorSubject<boolean>(false);
  
  // Event subjects for real-time updates
  private incidentCreated$ = new BehaviorSubject<any>(null);
  private incidentUpdated$ = new BehaviorSubject<any>(null);
  private vehicleAssigned$ = new BehaviorSubject<any>(null);
  private crewAssigned$ = new BehaviorSubject<any>(null);
  private additionalVehicleRequested$ = new BehaviorSubject<any>(null);
  private additionalVehicleAssigned$ = new BehaviorSubject<any>(null);
  private participantStatusUpdated$ = new BehaviorSubject<any>(null);

  constructor(private tokenStorage: TokenStorageService) {}

  connect(): void {
    const token = JSON.parse(localStorage.getItem('currentUser')!)?.token?.token;
    
    if (!token) {
      console.error('No token available for socket connection');
      return;
    }

    if (this.socket?.connected) {
      console.log('Socket already connected');
      return;
    }

    // Get base URL from environment or use default
    const socketUrl = environment.socketUrl;

    this.socket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      auth: { token },
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
    });

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      console.log('Socket connected:', this.socket?.id);
      this.connected$.next(true);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      this.connected$.next(false);
      
      if (reason === 'io server disconnect') {
        // Server forced disconnect, reconnect manually
        this.socket?.connect();
      }
    });

    this.socket.on('connect_error', (err) => {
      console.error('Socket connection error:', err.message);
      this.connected$.next(false);
    });

    this.socket.io.on('reconnect_attempt', () => {
      console.log('Socket reconnecting...');
    });

    this.socket.io.on('reconnect', () => {
      console.log('Socket reconnected');
      this.connected$.next(true);
    });

    // Incident events
    this.socket.on('incident.created', (data) => {
      console.log('New incident created:', data);
      this.incidentCreated$.next(data);
    });

    this.socket.on('incident.updated', (data) => {
      console.log('Incident updated:', data);
      this.incidentUpdated$.next(data);
    });

    this.socket.on('incident.vehicle_assigned', (data) => {
      console.log('Vehicle assigned:', data);
      this.vehicleAssigned$.next(data);
    });

    this.socket.on('incident.crew_assigned', (data) => {
      console.log('Crew assigned:', data);
      this.crewAssigned$.next(data);
    });

    this.socket.on('incident.additional_vehicle_requested', (data) => {
      console.log('Additional vehicle requested:', data);
      this.additionalVehicleRequested$.next(data);
    });

    this.socket.on('incident.additional_vehicle_assigned', (data) => {
      console.log('Additional vehicle assigned:', data);
      this.additionalVehicleAssigned$.next(data);
    });

    this.socket.on('incident.participant_status_updated', (data) => {
      console.log('Participant status updated:', data);
      this.participantStatusUpdated$.next(data);
    });
  }

  // Join/leave incident rooms
  joinIncident(incidentId: number): void {
    if (this.socket?.connected) {
      this.socket.emit('join-incident', incidentId);
      console.log('Joined incident room:', incidentId);
    }
  }

  leaveIncident(incidentId: number): void {
    if (this.socket?.connected) {
      this.socket.emit('leave-incident', incidentId);
      console.log('Left incident room:', incidentId);
    }
  }

  // Observable getters for components to subscribe
  get isConnected$(): Observable<boolean> {
    return this.connected$.asObservable();
  }

  get onIncidentCreated$(): Observable<any> {
    return this.incidentCreated$.asObservable();
  }

  get onIncidentUpdated$(): Observable<any> {
    return this.incidentUpdated$.asObservable();
  }

  get onVehicleAssigned$(): Observable<any> {
    return this.vehicleAssigned$.asObservable();
  }

  get onCrewAssigned$(): Observable<any> {
    return this.crewAssigned$.asObservable();
  }

  get onAdditionalVehicleRequested$(): Observable<any> {
    return this.additionalVehicleRequested$.asObservable();
  }

  get onAdditionalVehicleAssigned$(): Observable<any> {
    return this.additionalVehicleAssigned$.asObservable();
  }

  get onParticipantStatusUpdated$(): Observable<any> {
    return this.participantStatusUpdated$.asObservable();
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected$.next(false);
      console.log('Socket disconnected');
    }
  }
}
